import * as XtBase from "@xt/xtbase";
import { post_passwdPublicKey } from "@/api/uaapi/uaa";

function getXtUtil() {
  return XtBase?.Util;
}

function getXtAxios() {
  return XtBase?.Request?.XtAxios;
}

function getXtStorage() {
  return XtBase?.Storage;
}

function getXtPlatform() {
  return XtBase?.Platform;
}

function getOriginValue(subdomain) {
  const util = getXtUtil();
  const getOrigin = util?.getOrigin;
  return typeof getOrigin === "function" ? getOrigin(subdomain) : "";
}

const URL = () => getOriginValue("www") + "/rfapi";
const COUPON_URL = () => getOriginValue("www") + "/acapi";
/** 奖励中心 C 端接口（rc/mc/ma/result 封装），XtAxios 会解包为 result */
const SAPI_URL = () => getOriginValue("www") + "/sapi/v4";

/** 奖励中心活动接口是否走 mock 路径：true = /activity/mock/xxx，false = /activity/xxx（正式） */
export const ACTIVITY_USE_MOCK = false;

const ACTIVITY_BASE = () => `${SAPI_URL()}/activity/${ACTIVITY_USE_MOCK ? "mock/" : ""}`;

async function getClaimDeviceHeaders() {
  const platform = getXtPlatform();
  const util = getXtUtil();
  const storage = getXtStorage();
  if (!platform || typeof platform.isServer !== "function" || platform.isServer()) return {};

  const getUUID = util?.getUUID;
  const doRsaEncrypt = util?.doRsaEncrypt;
  const getExplore = util?.getExplore;
  const getPlatform = util?.getPlatform;
  if (typeof getUUID !== "function" || typeof doRsaEncrypt !== "function") return {};

  const storedDeviceId = storage?.get?.("client-device-id");
  const deviceId = storedDeviceId || getUUID();
  if (deviceId && !storedDeviceId && typeof storage?.set === "function") {
    storage.set("client-device-id", deviceId);
  }

  try {
    const res = await post_passwdPublicKey({ errorPop: false });
    const publicKey = res?.publicKey;
    if (!publicKey || !deviceId) return {};

    return {
      "client-device-id": doRsaEncrypt(publicKey, deviceId),
      "client-device-name": `${typeof getExplore === "function" ? getExplore() : "Unknown"} (${typeof getPlatform === "function" ? getPlatform() : "Unknown"})`,
    };
  } catch (e) {
    console.error("[activity] get claim device headers failed", e);
    return {};
  }
}

/** 可参与活动列表（需登录），返回 result 数组 */
/**
 * GET /activity/list — 获取可参与活动列表，返回 ActivitySummaryVO[]（result）。
 * 支持未登录（无 user-id 时无个人进度/奖励状态，可由 config.headers 传 user-id）。
 * 默认列表不含用户终局态：userStatus 为 COMPLETED / FAILED 的活动不会出现。
 *
 * @param {object} [config]
 * @param {object} [config.params]
 * @param {boolean} [config.params.ongoingOnly] — 为 true 时仅返回用户侧仍为进行中的活动
 *   （NOT_JOINED / PENDING_ACTIVATION / IN_PROGRESS），与 GET /progress 一致；未登录时为空列表。
 */
export function getActivityList(config) {
  return getXtAxios().get(`${ACTIVITY_BASE()}list`, { ...config, isAutoPopError: false });
}

/** 未注册时获取首页活动（公开接口，无需登录），返回 result 为单个活动对象，非列表 */
export function getPublicActivity(config) {
  return getXtAxios().get(`${SAPI_URL()}/public/activity/list`, { ...config, isAutoPopError: false });
}

/** 首页快捷入口上下文摘要（未登录） */
export function getPublicActivityContextSummary(config) {
  return getXtAxios().get(`${SAPI_URL()}/public/activity/context/summary`, { ...config, isAutoPopError: false });
}

/** 首页快捷入口上下文摘要（已登录） */
export function getActivityContextSummary(config) {
  return getXtAxios().get(`${SAPI_URL()}/activity/context/summary`, { ...config, isAutoPopError: false });
}

/** 未登录时获取单个活动详情（公开接口） */
export function getPublicActivityDetail(id, config) {
  return getXtAxios().get(`${SAPI_URL()}/public/activity/detail/${id}`, { ...config, isAutoPopError: false });
}

/** 查询正在进行中的任务卡片列表（含 cardType / tasks） */
export function getActivityOngoing(config) {
  return getXtAxios().get(`${ACTIVITY_BASE()}ongoing`, { ...config, isAutoPopError: false });
}

/** 获取用户奖励列表 */
export function getActivityRewards(config) {
  return getXtAxios().get(`${ACTIVITY_BASE()}rewards`, { ...config, isAutoPopError: false });
}

/** 获取单个活动详情 */
export function getActivityDetail(id, config) {
  return getXtAxios().get(`${ACTIVITY_BASE()}detail/${id}`, { ...config, isAutoPopError: false });
}

/** 获取单个奖励详情 */
export function getRewardDetail(id, config) {
  return getXtAxios().get(`${ACTIVITY_BASE()}rewards/${id}`, { ...config, isAutoPopError: false });
}

/** 参加活动 */
export function joinActivity(id, config) {
  return getXtAxios().post(`${ACTIVITY_BASE()}${id}/join`, null, { ...config, isAutoPopError: false });
}

/** 查询活动参与状态：NOT_JOINED / IN_PROGRESS / COMPLETED */
export function getParticipation(id, config) {
  return getXtAxios().get(`${ACTIVITY_BASE()}${id}/participation`, { ...config, isAutoPopError: false });
}

/** 查询当前用户在所有活动下的进度汇总 */
export function getActivityProgress(config) {
  return getXtAxios().get(`${ACTIVITY_BASE()}progress`, { ...config, isAutoPopError: false });
}

/** 查询锁定奖励详情 */
export function getRewardLockDetails(id, config) {
  return getXtAxios().get(`${ACTIVITY_BASE()}rewards/${id}/lock-details`, { ...config, isAutoPopError: false });
}

/** 领取奖励。userRewardRecordId 须来自 rewards[].userRewardRecordId */
export async function claimActivityReward(userRewardRecordId, config) {
  const deviceHeaders = await getClaimDeviceHeaders();
  return getXtAxios().post(`${ACTIVITY_BASE()}rewards/${userRewardRecordId}/claim`, null, {
    ...config,
    isAutoPopError: false,
    headers: {
      ...(config?.headers || {}),
      ...deviceHeaders,
    },
  });
}

/**
 * Past Tab：已结束或挑战失败/已完成等不可再推进的活动。
 * 结构同 GET /activity/list，按 endTime 倒序。
 * @param {object} [config] — 可传 params：participationOutcome（ALL | FAILED | COMPLETED | JOINED_ENDED | NOT_PARTICIPATED_EXPIRED，默认 ALL）、activityStatus（如 PUBLISHED、ENDED）
 */
export function getActivityListPast(config) {
  return getXtAxios().get(`${ACTIVITY_BASE()}list/past`, { ...config, isAutoPopError: false });
}

export function get_status(config) {
  return getXtAxios().get(`${URL()}/campaign/status`, config);
}

// 合伙人申请
export function post_join(config) {
  return getXtAxios().post(`${URL()}/campaign/join`, null, config);
}

// 通过兑换码领取 voucher
export function claimVoucherByCode(code, config) {
  return getXtAxios().post(`${COUPON_URL()}/welfare/user-coupon/exchange`, { code }, config);
}

// Voucher 使用历史
export function getVoucherHistory(config) {
  return getXtAxios().get(`${COUPON_URL()}/welfare/user-coupon/history`, config);
}
